// ABOUTME: Me class representing the authenticated user with computed properties.
// ABOUTME: Provides role detection, subscription status, and access to all user data.

import { Role, type UserInfo } from "@/graphql/main/gql/graphql"
import { toDate } from "@/model/date"
import type { User } from "./user"

/** Whether a role string (already upper-cased) names a Role. */
const isRole = (value: string): value is Role => Object.values<string>(Role).includes(value)

export class Me {
  constructor(
    private _user: UserInfo,
    private _userInfo: User,
  ) {}

  get user() {
    return this._user
  }

  get userInfo() {
    return this._userInfo
  }

  get info() {
    return this._userInfo.info
  }

  get role(): Role {
    const role = this.user.role.toUpperCase()
    return isRole(role) ? role : Role.Athlete
  }

  get isCoach(): boolean {
    return this.info.permissions?.trainer || this.role === Role.Trainer
  }

  get isPro(): boolean {
    const pro = this.info.permissions?.proUntil ?? undefined

    return isSubscriptionValid(pro)
  }

  get isAthlete(): boolean {
    return this.role === Role.Athlete
  }

  get id(): string {
    return this.user.id
  }

  get isOnboardingFilled(): boolean | null | undefined {
    return this.info.onboardingFilled
  }
}

/**
 * Whether a subscription running until `until` (`proUntil`, `coachPaidUntil` — server timestamps) is still on.
 * It ends at that very moment, not at the end of its day: the server refuses PRO features from then on
 * (gql-api IsAthletePro, `proUntil > now()`), and a check by day kept them open for up to a day longer.
 */
export function isSubscriptionValid(until: string | undefined): boolean {
  if (!until) return false

  return toDate(until).getTime() > Date.now()
}

/**
 * Whether the coach's paid-until date (`paidBefore`) still covers today. It is the coach's own record, which
 * the server does not enforce, so the whole of that day counts.
 */
export function isPaidThrough(serverDateStr: string | undefined): boolean {
  if (!serverDateStr) return false

  const today = new Date()
  const paidDate = toDate(serverDateStr)

  const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const paidDateOnly = new Date(paidDate.getFullYear(), paidDate.getMonth(), paidDate.getDate())

  return paidDateOnly >= todayDateOnly
}
